import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ProcessingStep } from '@/lib/api/processing/initJobs';
import { retryProcessingStep } from '@/lib/api/processing/initJobs';

export type ProcessingStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped'
  // Parked because the owner ran out of credits. Not a failure, and it never
  // resolves on its own, so it must not count as work in progress.
  | 'waiting_for_credits';

export interface ProcessingJob {
  id: string;
  paper_id: string;
  step: ProcessingStep | 'search_indexing';
  status: ProcessingStatus;
  progress: number;
  message: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

const STEP_ORDER: Array<ProcessingJob['step']> = [
  'metadata_extraction',
  'research_overview',
  'ai_review',
  'search_indexing',
];

export const STEP_LABELS: Record<ProcessingJob['step'], string> = {
  metadata_extraction: 'Extracting metadata & authors',
  research_overview: 'Generating research overview',
  ai_review: 'AI peer review',
  search_indexing: 'Indexing for search',
};

const ETA_DEFAULTS_MS: Record<ProcessingJob['step'], number> = {
  metadata_extraction: 8_000,
  research_overview: 15_000,
  ai_review: 35_000,
  search_indexing: 5_000,
};

const ETA_STORAGE_KEY = 'paperverse:processing-eta-v1';

function readEtaCache(): Record<string, number[]> {
  try {
    const raw = localStorage.getItem(ETA_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function writeEtaSample(step: ProcessingJob['step'], ms: number) {
  try {
    const cache = readEtaCache();
    const arr = cache[step] || [];
    arr.push(ms);
    cache[step] = arr.slice(-10);
    localStorage.setItem(ETA_STORAGE_KEY, JSON.stringify(cache));
  } catch { /* noop */ }
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function getEtaMs(step: ProcessingJob['step']): number {
  const cache = readEtaCache();
  return median(cache[step] || []) ?? ETA_DEFAULTS_MS[step];
}

export interface UsePaperProcessingStatusResult {
  jobs: ProcessingJob[];
  orderedJobs: ProcessingJob[];
  isProcessing: boolean;
  hasErrors: boolean;
  isComplete: boolean;
  retry: (step: ProcessingJob['step']) => Promise<void>;
}

/**
 * Subscribe to paper_processing_jobs for the given paper. Realtime push,
 * no polling. Cleanup tears down the channel on unmount.
 */
export function usePaperProcessingStatus(paperId: string | null | undefined): UsePaperProcessingStatusResult {
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);

  // Initial fetch (separate from subscription effect per Realtime Hook Pattern memory).
  useEffect(() => {
    let cancelled = false;
    if (!paperId) {
      setJobs([]);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('paper_processing_jobs')
        .select('*')
        .eq('paper_id', paperId);
      if (!cancelled && !error && data) {
        setJobs(data as ProcessingJob[]);
      }
    })();
    return () => { cancelled = true; };
  }, [paperId]);

  // Realtime subscription.
  useEffect(() => {
    if (!paperId) return;

    const channel = supabase
      .channel(`paper:${paperId}:processing`, { config: { private: true } })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'paper_processing_jobs',
          filter: `paper_id=eq.${paperId}`,
        },
        (payload) => {
          setJobs((prev) => {
            const next = [...prev];
            const eventType = (payload as { eventType?: string }).eventType;

            if (eventType === 'DELETE') {
              const oldRow = payload.old as ProcessingJob;
              return next.filter((j) => j.id !== oldRow.id);
            }
            const row = payload.new as ProcessingJob;
            if (!row?.id) return prev;

            const idx = next.findIndex((j) => j.id === row.id);
            if (idx >= 0) {
              const prevRow = next[idx];
              // Capture ETA sample on completion
              if (
                prevRow.status === 'running' &&
                row.status === 'completed' &&
                prevRow.started_at &&
                row.finished_at
              ) {
                const ms = new Date(row.finished_at).getTime() - new Date(prevRow.started_at).getTime();
                if (ms > 500 && ms < 10 * 60_000) writeEtaSample(row.step, ms);
              }
              next[idx] = row;
            } else {
              next.push(row);
            }
            return next;
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [paperId]);

  const orderedJobs = useMemo(
    () =>
      [...jobs].sort(
        (a, b) => STEP_ORDER.indexOf(a.step) - STEP_ORDER.indexOf(b.step),
      ),
    [jobs],
  );

  const isProcessing = useMemo(
    () => orderedJobs.some((j) => j.status === 'pending' || j.status === 'running'),
    [orderedJobs],
  );

  const hasErrors = useMemo(
    () => orderedJobs.some((j) => j.status === 'failed'),
    [orderedJobs],
  );

  const isComplete = useMemo(
    () =>
      orderedJobs.length > 0 &&
      orderedJobs.every((j) => j.status === 'completed' || j.status === 'skipped'),
    [orderedJobs],
  );

  const retry = useCallback(
    async (step: ProcessingJob['step']) => {
      if (!paperId) return;
      // Only client-managed steps are retryable from this UI.
      if (step === 'search_indexing') return;
      await retryProcessingStep(paperId, step as ProcessingStep);
      // Note: actual edge-function re-invocation is the caller's responsibility
      // (not done here to avoid double-invoking AI generation by accident).
    },
    [paperId],
  );

  return { jobs, orderedJobs, isProcessing, hasErrors, isComplete, retry };
}
